/* global window */
import React from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import Event from './Event';

export class LayeringBuffer {
  // holds data about last events while they are still relevant in layering
  constructor(layerCount) {
    this.layerCount = layerCount;
    this.buffer = Array(layerCount).fill([]);
  }

  getLayer(x) {
    // remove events that are far enough from the buffer
    this.buffer = this.buffer.map(
      bufferLayer => bufferLayer.filter(
        bx => (x - bx) < config.MAX_EVENT_WIDTH + config.EVENT_MARGIN,
      ),
    );
    // find the amount of elements in the least populated layer
    const targetItemCount = Math.min(...this.buffer.map(bufferLayer => bufferLayer.length));
    // find the first layer that matches the target amount
    const layersWithTargetItems = [];
    this.buffer.forEach((layer, index) => {
      if (layer.length === targetItemCount) layersWithTargetItems.push(index);
    });

    let layer = layersWithTargetItems[0];
    if (layersWithTargetItems.length > 1) {
      for (let i = 1; i < layersWithTargetItems.length; i += 1) {
        const currentMin = this.buffer[layer][this.buffer[layer].length - 1];
        const valueToCompare = this.buffer[i][this.buffer[i].length - 1];
        if (valueToCompare < currentMin) {
          layer = i;
        }
      }
    }
    this.buffer[layer].push(x);
    return [layer, targetItemCount];
  }
}

export class Events extends React.Component {
  constructor(props) {
    super(props);
    this.resizeTimeout = null;
    this.rootRef = React.createRef();
    this.updateLayerCountWhenResizeEnds = this.updateLayerCountWhenResizeEnds.bind(this);
    this.updateLayerCount = this.updateLayerCount.bind(this);
    this.state = {
      layerCount: 1,
    };
  }

  // Update layer count when window resizes
  componentDidMount() {
    this.updateLayerCount();
    window.addEventListener('resize', this.updateLayerCountWhenResizeEnds);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateLayerCountWhenResizeEnds);
  }

  setLayerCount(count) {
    this.setState({ layerCount: count });
  }

  updateLayerCountWhenResizeEnds() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(this.updateLayerCount, 200);
  }

  updateLayerCount() {
    const height = this.rootRef.current.clientHeight;
    // Set layer count based on element height and between [1-4]
    const layerCount = Math.min(
      Math.max(
        Math.floor(height / (config.MAX_EVENT_HEIGHT + config.EVENT_MARGIN)), 1,
      ), 4,
    );
    this.setState({ layerCount });
  }

  render() {
    const {
      elephant, start, events, onEventSelected,
      focusedDate, onElephantSelected, yearWidth,
    } = this.props;
    const { layerCount } = this.state;
    const layeringBuffer = new LayeringBuffer(layerCount);

    return (
      <div
        ref={this.rootRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          flexShrink: '1',
          flexGrow: '1',
          maxHeight: `${config.MAX_LAYER_COUNT * (config.MAX_EVENT_HEIGHT + config.EVENT_MARGIN)}px`,
        }}
      >
        <div style={{
          position: 'relative',
          height: `${layerCount * (config.MAX_EVENT_HEIGHT + config.EVENT_MARGIN)}px`,
        }}
        >
          {events.filter(event => event.type !== 'logbook_start' && event.type !== 'logbook_end')
            .map((event) => {
              const x = event.date.diff(start, 'days') / 365 * yearWidth;
              const [layer, itemsInLayer] = layeringBuffer.getLayer(x);
              const y = (layerCount - layer - 1) * (config.MAX_EVENT_HEIGHT + config.EVENT_MARGIN);
              const yShift = itemsInLayer * 8;

              return (
                <Event
                  key={event.key}
                  elephant={elephant}
                  layer={layer}
                  x={x}
                  y={y}
                  yShift={yShift}
                  event={event}
                  onSelected={onEventSelected}
                  onElephantSelected={onElephantSelected}
                  focused={event.date.isSame(focusedDate)}
                />
              );
            })}
        </div>
      </div>
    );
  }
}

Events.propTypes = {
  yearWidth: PropTypes.number.isRequired,
  start: PropTypes.shape().isRequired,
  elephant: PropTypes.shape().isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    date: PropTypes.shape(),
    title: PropTypes.string,
    details: PropTypes.string,
  })).isRequired,
  focusedDate: PropTypes.shape(),
  onEventSelected: PropTypes.func,
  onElephantSelected: PropTypes.func,
};
Events.defaultProps = {
  onEventSelected: () => {},
  focusedDate: null,
  onElephantSelected: () => {},
};

export default Events;



// WEBPACK FOOTER //
// ./src/components/lifeline/Events/Events.jsx